import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import AuthPage from "../../components/auth/AuthPage";
import SubmitButton from "../../components/auth/SubmitButton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { API } from "../../constant/api";
import { useUser } from "../../context/user-context";
import { forgotPasswordSchema } from "../../utils/authSchemas";
import { errorMessage } from "../../utils/errorMessage";
import sessionAxios from "../../utils/sessionAxios";

type ForgotPasswordValues = { email: string };

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { setResetEmail } = useUser();
  const [loading, setLoading] = useState(false);

  const form = useForm<ForgotPasswordValues>({
    resolver: yupResolver(forgotPasswordSchema) as any,
    defaultValues: { email: "" },
    mode: "onTouched",
  });

  const onSubmit = (values: ForgotPasswordValues) => {
    if (loading) return;
    setLoading(true);
    setResetEmail(values.email);

    sessionAxios
      .post(API.forgotPassword, { email: values.email })
      .then((r: any) => {
        setLoading(false);
        toast.success(r.data.message);
        navigate("/reset-password");
      })
      .catch((e: any) => {
        setLoading(false);
        toast.error(
          errorMessage(
            e?.response?.data?.message || "An error occurred. Please try again"
          )
        );
      });
  };

  return (
    <AuthPage
      title="👀 Forgot Password"
      description="Confirm your email and we'd send you instructions."
      footer={
        <span className="text-foreground">
          Don't have an account?{" "}
          <Link to="/signup" className="font-semibold text-primary underline">
            Sign Up
          </Link>
        </span>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="e.g. email@domain.com"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <SubmitButton loading={loading}>Send instructions</SubmitButton>
        </form>
      </Form>
    </AuthPage>
  );
};

export default ForgotPassword;
